//
// Copyright 2024 DXOS.org
//

import * as EffectContext from 'effect/Context';
import * as Effect from 'effect/Effect';
import * as Function from 'effect/Function';
import * as FetchHttpClient from 'effect/http/FetchHttpClient';
import * as HttpClient from 'effect/http/HttpClient';
import * as HttpClientRequest from 'effect/http/HttpClientRequest';
import * as Layer from 'effect/Layer';
import * as Schema from 'effect/Schema';

import { type Context } from '@dxos/context';
import * as EffectEx from '@dxos/effect/EffectEx';
import { invariant } from '@dxos/invariant';
import { IdentityDid, SpaceId } from '@dxos/keys';
import { log } from '@dxos/log';
import { type ProcessProtocol } from '@dxos/protocols';
import {
  type CompleteOAuthRegistrationRequest,
  type CompleteOAuthRegistrationResponse,
  type CreateAgentRequestBody,
  type CreateAgentResponseBody,
  EDGE_CLIENT_TAG_HEADER,
  type EdgeStatus,
  type ExecuteWorkflowResponseBody,
  type FeedProtocol,
  type GetAccessTokenRequest,
  type GetAccessTokenResponseBody,
  type GetAgentStatusResponseBody,
  type GetNotarizationResponseBody,
  type GetPluginsResponseBody,
  type InboxListResponse,
  InboxListResponseSchema,
  type InboxSendResponse,
  InboxSendResponseSchema,
  type IndexerHeadsResponse,
  type InitiateOAuthFlowRequest,
  type InitiateOAuthFlowResponse,
  type JoinSpaceRequest,
  type JoinSpaceResponseBody,
  type ObjectId,
  type PostNotarizationRequestBody,
  type RecoverIdentityRequest,
  type RecoverIdentityResponseBody,
  type SerializedError,
  type UploadFunctionRequest,
  type UploadFunctionResponseBody,
} from '@dxos/protocols';
import {
  type QueryRequest as QueryRequestProto,
  type QueryResponse as QueryResponseProto,
} from '@dxos/protocols/buf/dxos/echo/query_pb';
import { createUrl } from '@dxos/util';

import { BaseHttpClient, type BaseHttpClientOptions, type EdgeHttpCallArgs } from './base-http-client.ts';
import { proxyFetchLegacy } from './cors-proxy.ts';
import { HttpConfig, withLogging, withRetryConfig } from './http-client.ts';

export type { EdgeHttpCallArgs, RetryConfig } from './base-http-client.ts';

/** Arguments for {@link EdgeHttpClient.request}; authenticates unless `auth` is `false`. */
export type EdgeRequestArgs = EdgeHttpCallArgs & {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON-serializable request body. */
  body?: unknown;
};

/**
 * HTTP wire shape returned by `/queue/.../query`.
 */
export type EdgeQueryQueueResponse = {
  objects?: unknown[];
  nextCursor?: string;
  prevCursor?: string;
};

export type UploadPluginBundleRequest = {
  slug: string;
  version: string;
  files: { path: string; content: string }[];
};

export type CreateApiTokenRequest = {
  label: string;
  /** Epoch milliseconds; the token never expires when omitted. */
  expiresAt?: number;
};

/** A minted token: `token` is the secret, returned once; the rest is its non-secret summary. */
export type CreateApiTokenResponse = {
  token: string;
  id: string;
  prefix: string;
  label: string;
  expiresAt?: number | null;
};

export type TriggersDispatcherStatus = {
  isActive: boolean;
  nextCronTaskRunTimestamp?: number;
  registeredTriggers: string[];
  stopAfterTimestamp?: number;
  remainingMs?: number;
  nextAlarmTimestamp?: number;
};

export type GetCronTriggersResponse = {
  cronIds: string[];
};

/**
 * Per-trigger runtime status reported by the EDGE dispatcher, keyed to the
 * trigger's ECHO object id so a client can correlate it with the replicated
 * `Trigger` object in its local database.
 *
 * Served by compute-service at `GET /compute/triggers/{spaceId}` (see
 * {@link EdgeHttpClient.getSpaceTriggers}).
 */
export type EdgeTriggerStatus = {
  /** ECHO object id of the trigger. */
  triggerId: ObjectId;
  /** Whether the EDGE dispatcher currently has this trigger registered. */
  registered: boolean;
  kind: 'timer' | 'subscription' | 'email' | 'webhook' | 'feed' | 'direct';
  /** Next scheduled cron execution (epoch ms). Set only for `timer` triggers. */
  nextExecutionTimestamp?: number;
  /** Cooldown expiry after a failure (epoch ms). */
  cooldownUntilTimestamp?: number;
  /** Outcome of the most recent invocation on the edge. */
  lastResult?: {
    status: 'success' | 'failure';
    /** Completion time (epoch ms). */
    timestamp: number;
    error?: SerializedError;
  };
};

/**
 * Response of `GET /compute/triggers/{spaceId}`: the full list of triggers registered on a space's
 * EDGE dispatcher, with runtime status. Polled by the remote trigger monitor to surface edge
 * trigger state.
 */
export type GetSpaceTriggersResponse = {
  /** Whether the space's edge dispatcher is active. */
  isActive: boolean;
  triggers: EdgeTriggerStatus[];
};

export type EdgeHttpClientOptions = BaseHttpClientOptions;

/** What `finalize` reports back: the content-addressed key the bytes landed under, and their size. */
export type FinalizedUpload = { key: string; size: number; contentType?: string };

/** What EDGE answers every brain fact feed route with: the EIDs of the fact feeds the brain indexes. */
export const BrainFactFeeds = Schema.Struct({ feeds: Schema.Array(Schema.String) });
export type BrainFactFeeds = Schema.Schema.Type<typeof BrainFactFeeds>;

/** What EDGE answers `GET /compute/brain/:agent` with, as far as clients read it. */
export const BrainInfo = Schema.Struct({
  identity: Schema.Struct({ identityDid: Schema.String, label: Schema.String, defaultSpaceId: Schema.String }),
  role: Schema.String,
});
export type BrainInfo = Schema.Schema.Type<typeof BrainInfo>;

/** What EDGE answers an added admin with: the identity keys of every admin of the agent. */
export const BrainAdmins = Schema.Struct({ adminKeys: Schema.Array(Schema.String) });
export type BrainAdmins = Schema.Schema.Type<typeof BrainAdmins>;

/** What EDGE answers `GET /compute/brain/:agent/halo` with: what an admin inspects of an agent's identity. */
export const AgentHalo = Schema.Struct({
  identity: Schema.Struct({
    identityDid: Schema.String,
    identityKey: Schema.String,
    agentKey: Schema.String,
    defaultSpaceId: Schema.String,
    homeSpaceId: Schema.String,
    agent: Schema.String,
    label: Schema.String,
  }),
  haloSpaceId: Schema.optional(Schema.String),
  /** `null` for an agent provisioned before EDGE kept its admins. */
  adminKeys: Schema.NullOr(Schema.Array(Schema.String)),
  /** The spaces the agent's admins put it in. */
  joinedSpaceIds: Schema.Array(Schema.String),
  /** The spaces the agent holds a feed in, its HALO and default space included. */
  memberSpaceIds: Schema.Array(Schema.String),
  /** Every credential in the agent's HALO, each a base64 `Credential`. */
  credentials: Schema.Array(Schema.String),
});
export type AgentHalo = Schema.Schema.Type<typeof AgentHalo>;

/** What a client asks `POST /compute/brain` for: an identity for the agent with this entity id. */
export type ProvisionBrainRequest = { agent: string; homeSpaceId: SpaceId; label: string };

/** What EDGE answers `POST /compute/brain` with: the brain's identity and the caller's membership of its space. */
export const ProvisionedBrain = Schema.Struct({
  identity: Schema.Struct({
    identityDid: IdentityDid,
    identityKey: Schema.String,
    defaultSpaceId: Schema.optional(SpaceId),
  }),
  /** The caller's `SpaceMember` credential for the agent's default space, base64 protobuf. */
  membership: Schema.String,
});
export type ProvisionedBrain = Schema.Schema.Type<typeof ProvisionedBrain>;

/** EDGE route of an agent's mailbox. */
const mailboxPath = (handle: string): string => `/compute/email/mailboxes/${encodeURIComponent(handle)}`;

/**
 * Validates the finalize response rather than asserting its shape.
 *
 * The body is untrusted network input, and both fields are load-bearing downstream: `key` is fed
 * to `fromDigestHex`, whose parser does not reject malformed hex and would silently mint a bogus
 * `ni:` URI, and `size` is recorded on the Blob object as the authoritative byte count. A cast
 * would let either through.
 */
const parseFinalizeResponse = (body: unknown): FinalizedUpload => {
  if (typeof body !== 'object' || body === null) {
    throw new Error('Blob upload finalize returned a non-object body.');
  }
  const { key, size, contentType } = body as Record<string, unknown>;
  // 64 lowercase hex characters: the SHA-256 digest this store is keyed by, and nothing else.
  if (typeof key !== 'string' || !/^[0-9a-f]{64}$/.test(key)) {
    throw new Error('Blob upload finalize returned no valid content key.');
  }
  if (typeof size !== 'number' || !Number.isInteger(size) || size < 0) {
    throw new Error('Blob upload finalize returned no valid size.');
  }
  if (contentType !== undefined && typeof contentType !== 'string') {
    throw new Error('Blob upload finalize returned a non-string content type.');
  }
  return { key, size, ...(contentType === undefined ? {} : { contentType }) };
};

export class EdgeHttpClientService extends EffectContext.Service<EdgeHttpClientService, EdgeHttpClient>()(
  '@dxos/edge-client/EdgeHttpClient',
) {}

/**
 * HTTP client for the edge worker API (spaces, queues, functions, agents, etc.).
 *
 * Hub-service API (accounts, invitations) lives in {@link HubHttpClient} — the two
 * services run at different URLs and are never both available from the same base URL.
 */
/** Upstream service the EDGE AI proxy forwards to; selects the `/ai/generate/<service>` route. */
export type EdgeAiService =
  | 'anthropic'
  | 'deepseek'
  | 'typesafe'
  /** TypeSafe's System One wire, answered by Workers AI's `typesafe/jev` on EDGE's Cloudflare account. */
  | 'workers-ai/typesafe'
  /** Cloudflare's Clairic extraction model on Workers AI, on EDGE's Cloudflare account. */
  | 'workers-ai/clairic';

export class EdgeHttpClient extends BaseHttpClient {
  constructor(baseUrl: string, options?: EdgeHttpClientOptions) {
    super(baseUrl, options);
    log('created', { url: this.baseUrl });
  }

  //
  // Status
  //

  public async getStatus(ctx: Context, args?: EdgeHttpCallArgs): Promise<EdgeStatus> {
    return this._call(ctx, new URL('/status', this.baseUrl), { ...args, method: 'GET', auth: true });
  }

  //
  // Generic
  //

  /**
   * Authenticated JSON call to an EDGE route that has no dedicated method here (e.g. a service a
   * plugin owns end to end). Resolves to the response envelope's unwrapped `data`, left `unknown`
   * so the caller decodes it against its own schema.
   */
  public async request(ctx: Context, path: string, args: EdgeRequestArgs): Promise<unknown> {
    const { method, body, ...rest } = args;
    return this._call<unknown>(ctx, new URL(path, this.baseUrl), { auth: true, ...rest, method, body });
  }

  //
  // Agent mailboxes
  //
  // The mailbox answers are plugin-agent's to decode (`AgentMailbox.MailboxStatus`, `AgentMailbox.SendResult`).
  //

  /** Claims the agent's mailbox `handle`, or updates the claim; answers its status. */
  public async claimMailbox(ctx: Context, handle: string, body: unknown, args?: EdgeHttpCallArgs): Promise<unknown> {
    return this.request(ctx, mailboxPath(handle), { ...args, method: 'PUT', body });
  }

  /** Releases the mailbox `handle`; answers its status. */
  public async releaseMailbox(ctx: Context, handle: string, args?: EdgeHttpCallArgs): Promise<unknown> {
    return this.request(ctx, mailboxPath(handle), { ...args, method: 'DELETE' });
  }

  /** The status of the mailbox `handle`. */
  public async getMailbox(ctx: Context, handle: string, args?: EdgeHttpCallArgs): Promise<unknown> {
    return this.request(ctx, mailboxPath(handle), { ...args, method: 'GET' });
  }

  /** Sends an email from the mailbox `handle`; answers what EDGE sent. */
  public async sendMailboxEmail(
    ctx: Context,
    handle: string,
    body: unknown,
    args?: EdgeHttpCallArgs,
  ): Promise<unknown> {
    return this.request(ctx, `${mailboxPath(handle)}/send`, { ...args, method: 'POST', body });
  }

  //
  // Agents
  //

  public createAgent(
    ctx: Context,
    body: CreateAgentRequestBody,
    args?: EdgeHttpCallArgs,
  ): Promise<CreateAgentResponseBody> {
    return this._call(ctx, new URL('/identity/agents/create', this.baseUrl), {
      ...args,
      method: 'POST',
      body,
      auth: true,
    });
  }

  public getAgentStatus(
    ctx: Context,
    request: { ownerIdentityDid: string },
    args?: EdgeHttpCallArgs,
  ): Promise<GetAgentStatusResponseBody> {
    return this._call(ctx, new URL(`/identity/users/${request.ownerIdentityDid}/agent/status`, this.baseUrl), {
      ...args,
      method: 'GET',
      auth: true,
    });
  }

  //
  // Credentials
  //

  public getCredentialsForNotarization(
    ctx: Context,
    spaceId: SpaceId,
    args?: EdgeHttpCallArgs,
  ): Promise<GetNotarizationResponseBody> {
    return this._call(ctx, new URL(`/db/spaces/${spaceId}/notarization`, this.baseUrl), {
      ...args,
      method: 'GET',
      auth: true,
    });
  }

  public async notarizeCredentials(
    ctx: Context,
    spaceId: SpaceId,
    body: PostNotarizationRequestBody,
    args?: EdgeHttpCallArgs,
  ): Promise<void> {
    await this._call(ctx, new URL(`/db/spaces/${spaceId}/notarization`, this.baseUrl), {
      ...args,
      body,
      method: 'POST',
      auth: true,
    });
  }

  //
  // Identity
  //

  public async recoverIdentity(
    ctx: Context,
    body: RecoverIdentityRequest,
    args?: EdgeHttpCallArgs,
  ): Promise<RecoverIdentityResponseBody> {
    return this._call(ctx, new URL('/db/identity/recover', this.baseUrl), { ...args, body, method: 'POST' });
  }

  /**
   * Names the space's root document, which edge cannot derive, and returns the root in force —
   * not necessarily the one offered, since the record is write-once.
   */
  public async recordSpaceRoot(
    ctx: Context,
    spaceId: SpaceId,
    body: { rootDocumentUrl: string },
    args?: EdgeHttpCallArgs,
  ): Promise<{ rootDocumentUrl: string }> {
    return this._call(ctx, new URL(`/db/spaces/${spaceId}/root`, this.baseUrl), {
      ...args,
      body,
      method: 'POST',
      auth: true,
    });
  }

  /**
   * Asks edge to migrate a legacy space: edge mints the space root and credentials documents from the
   * chain it already holds and returns the root in force. Idempotent and write-once, so every member
   * that asks is handed the same root.
   */
  public async migrateSpaceRoot(
    ctx: Context,
    spaceId: SpaceId,
    args?: EdgeHttpCallArgs,
  ): Promise<{ rootDocumentUrl: string }> {
    return this._call(ctx, new URL(`/db/spaces/${spaceId}/root`, this.baseUrl), {
      ...args,
      method: 'POST',
      auth: true,
    });
  }

  /** The space root edge has in force, or null while the space is still on its control feed. */
  public async getSpaceRoot(
    ctx: Context,
    spaceId: SpaceId,
    args?: EdgeHttpCallArgs,
  ): Promise<{ rootDocumentUrl: string | null }> {
    return this._call(ctx, new URL(`/db/spaces/${spaceId}/root`, this.baseUrl), {
      ...args,
      method: 'GET',
      auth: true,
    });
  }

  //
  // Invitations (space join)
  //

  public async joinSpaceByInvitation(
    ctx: Context,
    spaceId: SpaceId,
    body: JoinSpaceRequest,
    args?: EdgeHttpCallArgs,
  ): Promise<JoinSpaceResponseBody> {
    return this._call(ctx, new URL(`/db/spaces/${spaceId}/join`, this.baseUrl), {
      ...args,
      body,
      method: 'POST',
      auth: true,
    });
  }

  //
  // Inbox (user-to-user notices)
  //

  /**
   * Leaves a notice in another identity's inbox; the sender is the identity this client authenticates as.
   * @param payload Opaque to EDGE; the recipient verifies it.
   */
  public async sendInboxMessage(
    ctx: Context,
    recipientDid: string,
    payload: string,
    args?: EdgeHttpCallArgs,
  ): Promise<InboxSendResponse> {
    const response = await this._call(ctx, new URL(`/inbox/${encodeURIComponent(recipientDid)}`, this.baseUrl), {
      ...args,
      body: { payload },
      method: 'POST',
      auth: true,
    });
    return Schema.decodeUnknownSync(InboxSendResponseSchema)(response);
  }

  /**
   * Lists the pending notices addressed to this client's identity.
   */
  public async listInbox(ctx: Context, args?: EdgeHttpCallArgs): Promise<InboxListResponse> {
    const response = await this._call(ctx, new URL('/inbox', this.baseUrl), { ...args, method: 'GET', auth: true });
    return Schema.decodeUnknownSync(InboxListResponseSchema)(response);
  }

  /**
   * Removes notices from this identity's inbox on every device.
   */
  public async ackInbox(ctx: Context, ids: readonly string[], args?: EdgeHttpCallArgs): Promise<void> {
    await this._call(ctx, new URL('/inbox/ack', this.baseUrl), {
      ...args,
      body: { ids },
      method: 'POST',
      auth: true,
    });
  }

  //
  // OAuth
  //

  public async initiateOAuthFlow(
    ctx: Context,
    body: InitiateOAuthFlowRequest,
    args?: EdgeHttpCallArgs,
  ): Promise<InitiateOAuthFlowResponse> {
    return this._call(ctx, new URL('/oauth/initiate', this.baseUrl), { ...args, body, method: 'POST', auth: true });
  }

  public async completeOAuthRegistration(
    ctx: Context,
    body: CompleteOAuthRegistrationRequest,
    args?: EdgeHttpCallArgs,
  ): Promise<CompleteOAuthRegistrationResponse> {
    return this._call(ctx, new URL('/oauth/registration/complete', this.baseUrl), { ...args, body, method: 'POST' });
  }

  /**
   * Resolves the live access token behind a `MANAGED_ACCESS_TOKEN` placeholder. Authorized by the
   * caller's presentation: EDGE serves it only to members of the owning space.
   */
  public async getAccessToken(
    ctx: Context,
    body: GetAccessTokenRequest,
    args?: EdgeHttpCallArgs,
  ): Promise<GetAccessTokenResponseBody> {
    return this._call(ctx, new URL('/oauth/token', this.baseUrl), { ...args, body, method: 'POST', auth: true });
  }

  //
  // Queues
  //

  public async queryQueue(
    ctx: Context,
    subspaceTag: string,
    spaceId: SpaceId,
    query: FeedProtocol.FeedQuery,
    args?: EdgeHttpCallArgs,
  ): Promise<EdgeQueryQueueResponse> {
    const queueId = query.feedIds?.[0];
    invariant(queueId, 'queueId required');
    return this._call(
      ctx,
      createUrl(new URL(`/db/spaces/${subspaceTag}/${spaceId}/queue/${queueId}/query`, this.baseUrl), {
        after: query.after,
        before: query.before,
        limit: query.limit,
        reverse: query.reverse,
        objectIds: query.objectIds?.join(','),
      }),
      { ...args, method: 'GET', auth: true },
    );
  }

  public async insertIntoQueue(
    ctx: Context,
    subspaceTag: string,
    spaceId: SpaceId,
    queueId: ObjectId,
    objects: unknown[],
    args?: EdgeHttpCallArgs,
  ): Promise<void> {
    return this._call(ctx, new URL(`/db/spaces/${subspaceTag}/${spaceId}/queue/${queueId}`, this.baseUrl), {
      ...args,
      body: { objects },
      method: 'POST',
      auth: true,
    });
  }

  public async deleteFromQueue(
    ctx: Context,
    subspaceTag: string,
    spaceId: SpaceId,
    queueId: ObjectId,
    objectIds: ObjectId[],
    args?: EdgeHttpCallArgs,
  ): Promise<void> {
    return this._call(
      ctx,
      createUrl(new URL(`/db/spaces/${subspaceTag}/${spaceId}/queue/${queueId}`, this.baseUrl), {
        ids: objectIds.join(','),
      }),
      { ...args, method: 'DELETE', auth: true },
    );
  }

  //
  // Blobs
  //

  /**
   * Builds the URL for the blob stored under `key`. `key` is URL-encoded for defense in depth —
   * callers pass a lowercase hex SHA-256 digest (extracted from an `ni:` URI by the edge backend).
   */
  public getBlobUrl(key: string): URL {
    return new URL(`/blob/file/${encodeURIComponent(key)}`, this.baseUrl);
  }

  /**
   * Uploads bytes to the edge blob service, keyed by content hash. Pre-fetches `/auth` (`auth:
   * true`) so large bodies aren't sent twice on an auth challenge.
   */
  public async putBlob(
    ctx: Context,
    key: string,
    data: Uint8Array,
    args?: EdgeHttpCallArgs & { contentType?: string },
  ): Promise<void> {
    const headers: Record<string, string> = {};
    if (args?.contentType) {
      headers['Content-Type'] = args.contentType;
    }
    await this._callRaw(ctx, this.getBlobUrl(key), {
      retry: args?.retry,
      auth: args?.auth ?? true,
      method: 'POST',
      // `Uint8Array` is generic over `ArrayBufferLike` (incl. `SharedArrayBuffer`) while DOM's
      // `BodyInit` only covers `ArrayBuffer`-backed views — a gap between the DOM lib types and
      // the TS standard lib, not fixable by typing `data` differently.
      body: data as BodyInit,
      headers,
    });
  }

  /**
   * Admits a completed direct upload into the content-addressed store, returning the key it landed
   * under along with what the service actually received.
   *
   * The bytes were PUT straight to a signed URL by a third party — typically an agent's `curl` —
   * so they never pass through this client; this call only tells the service to promote them. The
   * size and content type come from the service for the same reason.
   */
  public async finalizeBlobUpload(ctx: Context, uploadId: string, args?: EdgeHttpCallArgs): Promise<FinalizedUpload> {
    const url = new URL(`/blob/upload/${encodeURIComponent(uploadId)}/finalize`, this.baseUrl);
    const response = await this._callRaw(ctx, url, { ...args, method: 'POST', auth: args?.auth ?? true });
    return parseFinalizeResponse(await response.json());
  }

  /**
   * Downloads bytes previously stored with {@link putBlob}. Returns `undefined` if `key` is not
   * found.
   */
  public async getBlob(ctx: Context, key: string, args?: EdgeHttpCallArgs): Promise<Uint8Array | undefined> {
    const response = await this._callRaw(ctx, this.getBlobUrl(key), {
      ...args,
      method: 'GET',
      auth: args?.auth ?? true,
    });
    if (response.status === 404) {
      return undefined;
    }
    return new Uint8Array(await response.arrayBuffer());
  }

  /**
   * Checks whether bytes are stored under `key`, without downloading them.
   */
  public async hasBlob(ctx: Context, key: string, args?: EdgeHttpCallArgs): Promise<boolean> {
    const response = await this._callRaw(ctx, this.getBlobUrl(key), {
      ...args,
      method: 'HEAD',
      auth: args?.auth ?? true,
    });
    return response.status !== 404;
  }

  /**
   * Deletes bytes stored under `key`. Not called by any core `Blob.remove` path in v1 (deletion is
   * deferred), provided for completeness.
   */
  public async deleteBlob(ctx: Context, key: string, args?: EdgeHttpCallArgs): Promise<void> {
    await this._callRaw(ctx, this.getBlobUrl(key), { ...args, method: 'DELETE', auth: args?.auth ?? true });
  }

  //
  // Functions
  //

  public async uploadFunction(
    ctx: Context,
    pathParts: { functionId?: string },
    body: UploadFunctionRequest,
    args?: EdgeHttpCallArgs,
  ): Promise<UploadFunctionResponseBody> {
    const formData = new FormData();
    formData.append('name', body.name ?? '');
    formData.append('version', body.version);
    // The function owner is the authenticated identity (edge requires ownerUri === presenter DID).
    // Prefer the connected identity's DID; otherwise use the DID supplied on the request body.
    const ownerUri = this._edgeIdentity?.identityDid ?? body.ownerUri;
    formData.append('ownerUri', ownerUri);
    formData.append('entryPoint', body.entryPoint);
    body.runtime && formData.append('runtime', body.runtime);
    for (const [filename, content] of Object.entries(body.assets)) {
      formData.append(
        'assets',
        new Blob([content as Uint8Array<ArrayBuffer>], { type: getFileMimeType(filename) }),
        filename,
      );
    }
    const path = ['/compute/functions', ...(pathParts.functionId ? [pathParts.functionId] : [])].join('/');
    return this._call(ctx, new URL(path, this.baseUrl), {
      ...args,
      body: formData,
      method: 'PUT',
      json: false,
      auth: true,
    });
  }

  public async listFunctions(ctx: Context, args?: EdgeHttpCallArgs): Promise<any> {
    return this._call(ctx, new URL('/compute/functions', this.baseUrl), { ...args, method: 'GET', auth: true });
  }

  public async invokeFunction(
    ctx: Context,
    params: {
      functionId: string;
      version?: string;
      spaceId?: SpaceId;
      cpuTimeLimit?: number;
      subrequestsLimit?: number;
    },
    input: unknown,
    args?: EdgeHttpCallArgs,
  ): Promise<any> {
    const url = new URL(`/compute/functions/${params.functionId}`, this.baseUrl);
    if (params.version) {
      url.searchParams.set('version', params.version);
    }
    if (params.spaceId) {
      url.searchParams.set('spaceId', params.spaceId.toString());
    }
    if (params.cpuTimeLimit) {
      url.searchParams.set('cpuTimeLimit', params.cpuTimeLimit.toString());
    }
    if (params.subrequestsLimit) {
      url.searchParams.set('subrequestsLimit', params.subrequestsLimit.toString());
    }
    return this._call(ctx, url, { ...args, body: input, method: 'POST', auth: true });
  }

  //
  // Workflows
  //

  public async executeWorkflow(
    ctx: Context,
    spaceId: SpaceId,
    graphId: ObjectId,
    input: any,
    args?: EdgeHttpCallArgs,
  ): Promise<ExecuteWorkflowResponseBody> {
    return this._call(ctx, new URL(`/compute/workflows/${spaceId}/${graphId}`, this.baseUrl), {
      ...args,
      body: input,
      method: 'POST',
      auth: true,
    });
  }

  //
  // Triggers
  //

  public async getCronTriggers(ctx: Context, spaceId: SpaceId): Promise<GetCronTriggersResponse> {
    return this._call<GetCronTriggersResponse>(
      ctx,
      new URL(`/compute/functions/${spaceId}/triggers/crons`, this.baseUrl),
      {
        method: 'GET',
        auth: true,
      },
    );
  }

  public async getTriggersDispatcherStatus(
    ctx: Context,
    spaceId: SpaceId,
    args?: EdgeHttpCallArgs,
  ): Promise<TriggersDispatcherStatus> {
    return this._call<TriggersDispatcherStatus>(ctx, new URL(`/compute/triggers/${spaceId}/status`, this.baseUrl), {
      ...args,
      method: 'GET',
      auth: true,
    });
  }

  /**
   * Runs a cron trigger now. `data` becomes the fire event's `data`, which the trigger's
   * `{{event.data.*}}` input templates read, and is kept across the run's `runAgain` continuations.
   */
  public async forceRunCronTrigger(
    ctx: Context,
    spaceId: SpaceId,
    triggerId: ObjectId,
    { data }: { data?: Record<string, unknown> } = {},
  ) {
    return this._call(ctx, new URL(`/compute/functions/${spaceId}/triggers/crons/${triggerId}/run`, this.baseUrl), {
      method: 'POST',
      auth: true,
      ...(data ? { body: { data } } : {}),
    });
  }

  /**
   * Cancels the current run of a cron trigger on the EDGE dispatcher — its in-flight execution and
   * `runAgain` continuation chain. The trigger stays enabled, so its schedule keeps firing.
   */
  public async cancelTriggerRun(ctx: Context, spaceId: SpaceId, triggerId: ObjectId) {
    return this._call(ctx, new URL(`/compute/functions/${spaceId}/triggers/crons/${triggerId}/cancel`, this.baseUrl), {
      method: 'POST',
      auth: true,
    });
  }

  /**
   * Rewinds a feed trigger on the EDGE dispatcher to the start of its feed, so every item is delivered
   * again. EDGE answers 409 for a trigger that is not a feed trigger.
   */
  public async resetTrigger(ctx: Context, spaceId: SpaceId, triggerId: ObjectId) {
    return this._call<{ reset: boolean }>(
      ctx,
      new URL(`/compute/functions/${spaceId}/triggers/${triggerId}/reset`, this.baseUrl),
      { method: 'POST', auth: true },
    );
  }

  /**
   * Returns the full list of triggers registered on a space's EDGE dispatcher, with per-trigger
   * runtime status. Polled by the remote trigger monitor to surface edge trigger state.
   *
   * TODO(edge): Proposed endpoint; not yet implemented server-side.
   */
  public async getSpaceTriggers(
    ctx: Context,
    spaceId: SpaceId,
    args?: EdgeHttpCallArgs,
  ): Promise<GetSpaceTriggersResponse> {
    return this._call<GetSpaceTriggersResponse>(ctx, new URL(`/compute/triggers/${spaceId}`, this.baseUrl), {
      ...args,
      method: 'GET',
      auth: true,
    });
  }

  //
  // Brains
  //

  /**
   * The identity EDGE minted for an agent's brain, and the caller's role on it. `agent` is the agent's DID; EDGE answers
   * 404 to a caller with no role, as for a brain that does not exist.
   */
  public async getBrain(ctx: Context, agent: string, args?: EdgeHttpCallArgs): Promise<BrainInfo> {
    const url = new URL(`/compute/brain/${encodeURIComponent(agent)}`, this.baseUrl);
    const response = await this._call(ctx, url, { ...args, method: 'GET', auth: true });
    return Schema.decodeUnknownSync(BrainInfo)(response);
  }

  /** Gives the agent's brain an identity, for a caller who may write to its space; its creator asking again gets the same answer. */
  public async provisionBrain(
    ctx: Context,
    body: ProvisionBrainRequest,
    args?: EdgeHttpCallArgs,
  ): Promise<ProvisionedBrain> {
    const response = await this.request(ctx, '/compute/brain', { ...args, method: 'POST', body });
    return Schema.decodeUnknownSync(ProvisionedBrain)(response);
  }

  /** Puts the agent's brain in a space, which only its admins may do. `agent` is the agent's DID. */
  public async addBrainSpace(ctx: Context, agent: string, spaceId: SpaceId, args?: EdgeHttpCallArgs): Promise<void> {
    await this.request(ctx, `/compute/brain/${encodeURIComponent(agent)}/spaces`, {
      ...args,
      method: 'POST',
      body: { spaceId },
    });
  }

  /** Makes an identity an admin of an agent, which only an admin may do; nothing takes it away. Answers every admin. */
  public async addBrainAdmin(
    ctx: Context,
    agent: string,
    identityKey: string,
    args?: EdgeHttpCallArgs,
  ): Promise<BrainAdmins> {
    const url = new URL(`/compute/brain/${encodeURIComponent(agent)}/admins`, this.baseUrl);
    const response = await this._call(ctx, url, { ...args, method: 'POST', body: { identityKey }, auth: true });
    return Schema.decodeUnknownSync(BrainAdmins)(response);
  }

  /** The agent's HALO, its admins and its spaces, which only its admins may read; EDGE answers anyone else 404. */
  public async getAgentHalo(ctx: Context, agent: string, args?: EdgeHttpCallArgs): Promise<AgentHalo> {
    const url = new URL(`/compute/brain/${encodeURIComponent(agent)}/halo`, this.baseUrl);
    const response = await this._call(ctx, url, { ...args, method: 'GET', auth: true });
    return Schema.decodeUnknownSync(AgentHalo)(response);
  }

  //
  // Brain fact feeds
  //

  /**
   * Has an agent's brain index a fact feed (its EID) from now on; answers the feeds it indexes. `agent` names the
   * brain (see plugin-agent's `AgentPresence.brainId`); the caller must be a member of `spaceId`, a space it serves.
   */
  public async addBrainFactFeed(
    ctx: Context,
    spaceId: SpaceId,
    agent: string,
    feed: string,
    args?: EdgeHttpCallArgs,
  ): Promise<BrainFactFeeds> {
    return this.#factFeedsCall(ctx, spaceId, agent, { ...args, method: 'POST', body: { feed } });
  }

  /** Stops an agent's brain indexing a fact feed; the facts it read stay. Answers the feeds it still indexes. */
  public async removeBrainFactFeed(
    ctx: Context,
    spaceId: SpaceId,
    agent: string,
    feed: string,
    args?: EdgeHttpCallArgs,
  ): Promise<BrainFactFeeds> {
    return this.#factFeedsCall(ctx, spaceId, agent, { ...args, method: 'DELETE' }, { feed });
  }

  /** The fact feeds an agent's brain indexes. */
  public async listBrainFactFeeds(
    ctx: Context,
    spaceId: SpaceId,
    agent: string,
    args?: EdgeHttpCallArgs,
  ): Promise<BrainFactFeeds> {
    return this.#factFeedsCall(ctx, spaceId, agent, { ...args, method: 'GET' });
  }

  async #factFeedsCall(
    ctx: Context,
    spaceId: SpaceId,
    agent: string,
    args: EdgeHttpCallArgs & { method: string; body?: unknown },
    query: Record<string, string> = {},
  ): Promise<BrainFactFeeds> {
    const url = new URL(`/compute/brain/${spaceId}/${encodeURIComponent(agent)}/fact-feeds`, this.baseUrl);
    for (const [key, value] of Object.entries(query)) {
      url.searchParams.set(key, value);
    }
    const response = await this._call(ctx, url, { ...args, auth: true });
    return Schema.decodeUnknownSync(BrainFactFeeds)(response);
  }

  //
  // Query
  //

  public async execQuery(
    ctx: Context,
    spaceId: SpaceId,
    body: QueryRequestProto,
    args?: EdgeHttpCallArgs,
  ): Promise<QueryResponseProto> {
    return this._call(ctx, new URL(`/db/spaces/${spaceId}/exec-query`, this.baseUrl), {
      ...args,
      body,
      method: 'POST',
      auth: true,
    });
  }

  /** Heads of every document in the space as last indexed by EDGE. */
  public async getIndexerHeads(ctx: Context, spaceId: SpaceId, args?: EdgeHttpCallArgs): Promise<IndexerHeadsResponse> {
    return this._call(ctx, new URL(`/db/spaces/${spaceId}/indexer-heads`, this.baseUrl), {
      ...args,
      method: 'GET',
      auth: true,
    });
  }

  //
  // Registry
  //

  public async getRegistryPlugins(ctx: Context, args?: EdgeHttpCallArgs): Promise<GetPluginsResponseBody> {
    return this._call(ctx, new URL('/registry/plugins', this.baseUrl), { ...args, method: 'GET' });
  }

  /**
   * Uploads a built plugin bundle to the registry's R2-backed hosting. Authenticated
   * with the caller's hub identity (verifiable presentation) — `setIdentity` must
   * have been called. Returns the canonical `moduleUrl` (the hosted `manifest.json`).
   */
  public async uploadPluginBundle(
    ctx: Context,
    request: UploadPluginBundleRequest,
    args?: EdgeHttpCallArgs,
  ): Promise<{ moduleUrl: string }> {
    return this._call(ctx, new URL('/registry/upload', this.baseUrl), {
      body: request,
      method: 'POST',
      auth: true,
      ...args,
    });
  }

  /**
   * Your private plugins: releases uploaded with {@link uploadPrivatePluginBundle}, listed only to the
   * identity that published them. Same shape as {@link getRegistryPlugins}, so callers merge the two.
   */
  public async getPrivateRegistryPlugins(ctx: Context, args?: EdgeHttpCallArgs): Promise<GetPluginsResponseBody> {
    return this._call(ctx, new URL('/registry/private/plugins', this.baseUrl), { auth: true, ...args, method: 'GET' });
  }

  /**
   * Uploads a private plugin release, which is hosted like a public one but written to no AT Protocol
   * repo: the registry records it against the caller, authenticated by identity or by API key.
   */
  public async uploadPrivatePluginBundle(
    ctx: Context,
    request: UploadPluginBundleRequest,
    args?: EdgeHttpCallArgs,
  ): Promise<{ moduleUrl: string }> {
    return this._call(ctx, new URL('/registry/private/upload', this.baseUrl), {
      body: request,
      method: 'POST',
      auth: true,
      ...args,
    });
  }

  //
  // API tokens
  //

  /**
   * Mints a personal API token bound to this client's identity, for a process that cannot sign as it
   * (a CLI in a sandbox, a CI job). Hub accepts only a verifiable presentation here, never a token.
   */
  public async createApiToken(
    ctx: Context,
    request: CreateApiTokenRequest,
    args?: EdgeHttpCallArgs,
  ): Promise<CreateApiTokenResponse> {
    return this._call(ctx, new URL('/hub/api/api-tokens', this.baseUrl), {
      body: request,
      method: 'POST',
      auth: true,
      ...args,
    });
  }

  //
  // Proxy
  //

  /**
   * Fetch through the edge proxy for third-party REST APIs.
   * TEMPORARY: currently routes through legacy open proxy. See https://github.com/dxos/edge/pull/576.
   */
  public async proxyFetch(target: URL, init: RequestInit = {}): Promise<Response> {
    return proxyFetchLegacy(target, init, this._clientTag);
  }

  //
  // AI service.
  //

  /**
   * Issue an authenticated request to the EDGE AI route (`/ai/generate/<service>/*`), which proxies
   * to the AI service. Used as the backend HTTP client for the edge AI providers (see
   * {@link EdgeAiHttpClient}); `service` selects the upstream the proxy forwards to.
   *
   * Returns the raw `Response` so streaming bodies are forwarded unchanged to `@effect/ai`.
   * Requires an identity to have been set via {@link setIdentity}.
   */
  // TODO(mykola): Merge into `BaseHttpClient._call` once it can return a streaming/raw `Response`;
  // the auth/retry loop below duplicates the one in `_call`.
  public async aiRequest(service: EdgeAiService, request: Request): Promise<Response> {
    const incoming = new URL(request.url);
    const base = this.baseUrl.replace(/\/$/, '');
    const target = new URL(`${base}/ai/generate/${service}${incoming.pathname}${incoming.search}`);

    const method = request.method;
    const body = method === 'GET' || method === 'HEAD' ? undefined : await request.arrayBuffer();

    let handledAuth = false;
    while (true) {
      if (!this._authHeader) {
        await this._prefetchAuthHeader();
      }

      const headers = new Headers(request.headers);
      if (this._authHeader) {
        headers.set('Authorization', this._authHeader);
      }
      if (this._clientTag) {
        headers.set(EDGE_CLIENT_TAG_HEADER, this._clientTag);
      }

      // `redirect: 'error'` rather than the default 'follow': these headers carry the EDGE auth
      // credential and, on a BYOK request, the user's own provider key, and custom headers are not
      // guaranteed to be stripped on a cross-origin redirect.
      const response = await fetch(target, { method, headers, body, redirect: 'error', signal: request.signal });
      // Only retry edge auth when the 401 came from edge's own auth layer. Edge always sets
      // `WWW-Authenticate` on its own 401s; upstream-forwarded 401s (e.g. invalid BYOK rejected
      // by Anthropic) lack it and must be surfaced verbatim.
      if (response.status === 401 && response.headers.get('WWW-Authenticate') !== null && !handledAuth) {
        this._authHeader = await this._handleUnauthorized(response);
        handledAuth = true;
        continue;
      }

      return response;
    }
  }

  //
  // Internal (Effect-based, used by tests)
  //

  public async _fetch<T>(url: URL, _args: { method: string }): Promise<T> {
    return Function.pipe(
      HttpClient.execute(HttpClientRequest.make(_args.method as any)(url.toString())),
      withLogging,
      withRetryConfig,
      Effect.provide(Layer.provideMerge(FetchHttpClient.layer, HttpConfig.default)),
      Effect.withSpan('EdgeHttpClient'),
      EffectEx.runAndForwardErrors,
    ) as T;
  }

  //
  // Process control (see `ProcessProtocol`). The EDGE host runs `Process` instances — agents
  // first — that outlive the client; these are the routes that drive one.
  //

  /**
   * Spawns one of the EDGE host's built-in processes in `spaceId`. A process definition cannot cross
   * the wire, so the request names the process by its `Process.key`.
   */
  public async spawnProcess(
    ctx: Context,
    spaceId: SpaceId,
    body: ProcessProtocol.SpawnProcessRequest,
  ): Promise<ProcessProtocol.SpawnProcessResponse> {
    return this._call<ProcessProtocol.SpawnProcessResponse>(
      ctx,
      new URL(`/compute/processes/${spaceId}`, this.baseUrl),
      {
        body,
        method: 'POST',
        auth: true,
      },
    );
  }

  public async listProcesses(
    ctx: Context,
    spaceId: SpaceId,
    query?: ProcessProtocol.ListProcessesQuery,
  ): Promise<ProcessProtocol.ListProcessesResponse> {
    const url = new URL(`/compute/processes/${spaceId}`, this.baseUrl);
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
    return this._call<ProcessProtocol.ListProcessesResponse>(ctx, url, {
      method: 'GET',
      auth: true,
    });
  }

  public async getProcess(ctx: Context, spaceId: SpaceId, pid: string): Promise<ProcessProtocol.ProcessInfo> {
    return this._call<ProcessProtocol.ProcessInfo>(
      ctx,
      new URL(`/compute/processes/${spaceId}/${encodeURIComponent(pid)}`, this.baseUrl),
      {
        method: 'GET',
        auth: true,
      },
    );
  }

  /**
   * Terminates the process and clears its durable storage on the host.
   *
   * `idempotencyKey` travels as a query parameter rather than a body: the route is a DELETE, and a
   * body there is not reliably forwarded.
   */
  public async terminateProcess(
    ctx: Context,
    spaceId: SpaceId,
    pid: string,
    options?: { idempotencyKey?: ProcessProtocol.IdempotencyKey },
  ): Promise<void> {
    const url = new URL(`/compute/processes/${spaceId}/${encodeURIComponent(pid)}`, this.baseUrl);
    if (options?.idempotencyKey !== undefined) {
      url.searchParams.set('idempotencyKey', options.idempotencyKey);
    }
    await this._call(ctx, url, {
      method: 'DELETE',
      auth: true,
    });
  }

  /** Submits an input already encoded via the process definition's input schema. */
  public async submitProcessInput(
    ctx: Context,
    spaceId: SpaceId,
    pid: string,
    body: ProcessProtocol.SubmitInputRequest,
  ): Promise<void> {
    await this._call(ctx, new URL(`/compute/processes/${spaceId}/${encodeURIComponent(pid)}/input`, this.baseUrl), {
      body,
      method: 'POST',
      auth: true,
    });
  }

  /**
   * URL of the process's RPC endpoint. The surface is served as effect-rpc-over-HTTP, so callers
   * drive it with an `RpcClient` over this URL rather than through this client's JSON envelope;
   * {@link getAuthHeader} supplies the credential for those requests.
   */
  public processRpcUrl(spaceId: SpaceId, pid: string): URL {
    return new URL(`/compute/processes/${spaceId}/${encodeURIComponent(pid)}/rpc`, this.baseUrl);
  }

  /**
   * Reads the process's outputs and ephemeral trace at or after `cursor`, plus its state at read
   * time. Cursor-based so a client that reloads resumes an in-flight remote process where it left off.
   */
  public async readProcessEvents(
    ctx: Context,
    spaceId: SpaceId,
    pid: string,
    cursor: number,
  ): Promise<ProcessProtocol.ProcessEventsResponse> {
    const url = new URL(`/compute/processes/${spaceId}/${encodeURIComponent(pid)}/events`, this.baseUrl);
    url.searchParams.set('cursor', String(cursor));
    return this._call<ProcessProtocol.ProcessEventsResponse>(ctx, url, {
      method: 'GET',
      auth: true,
    });
  }
}

const getFileMimeType = (filename: string) =>
  ['.js', '.mjs'].some((ext) => filename.endsWith(ext))
    ? 'application/javascript+module'
    : filename.endsWith('.wasm')
      ? 'application/wasm'
      : 'application/octet-stream';
